// Libraries
import React, { Component, Fragment } from "react";
import { toast } from "react-toastify";
import _ from "lodash";

// Child Components
import Header from "../../common/header";
import FilesTable from "./filesTable";
import Expire from "../../common/expire";
import Search from "../../common/search";
import Filter from "../../common/filter";
import TableMeta from "../../common/table/tableMeta";
import Pagination from "../../common/pagination";

// Functions
import { paginate } from "../../../utils/scripts/paginate";
import {
  getFolders,
  getFiles,
  deleteFile,
} from "../../../services/fileService";

class Files extends Component {
  state = {
    images: [],
    folders: [],
    currentPage: 1,
    initialSize: 5,
    pageSize: 5,
    searchQuery: "",
    selectedFolder: null,
    sortColumn: { path: "name", order: "asc" },
  };

  // Retrive data from the server
  async componentDidMount() {
    // Get Folders and Modify
    const { data: allFolders } = await getFolders("public");
    if (allFolders.length === 0) return;

    // When at least a Folder exists in the directory
    const data = allFolders.map((folder) => ({
      _id: folder,
      name: folder,
    }));
    const folders = [{ _id: "", name: "All Files" }, ...data];

    // Get Files and Modify
    const { data: allFiles } = await getFiles("public");
    if (allFiles.length === 0) return;

    // When at least a file exists in the directory
    const images = allFiles.map(({ parent, name, url, type }) => ({
      type: this.getType(type),
      name,
      url,
      parent,
    }));

    this.setState({ images, folders });

    // Filter files by Query
    const searchParams = new URLSearchParams(window.location.search);
    const searchQuery = searchParams.get("folder");

    if (searchQuery) {
      const folder = folders.find((f) => f.name === searchQuery);

      this.setState({
        selectedFolder: folder,
        searchQuery: "",
        currentPage: 1,
      });
    }
  }

  // Get Files type
  getType(mime) {
    if (mime.startsWith("image")) {
      return "Image";
    } else if (mime.startsWith("video")) {
      return "Video";
    } else if (mime.startsWith("audio")) {
      return "Audio";
    } else {
      return "File";
    }
  }

  // Delete event handler
  handleDelete = async (image) => {
    // Delete File from the State
    const originalImages = this.state.images;
    const images = originalImages.filter((i) => i.name !== image.name);

    this.setState({ images });

    try {
      const response = await deleteFile("public", image.name);

      if (response.data && response.status === 200) {
        toast.success(response.data.message);
      }
    } catch (ex) {
      if (ex.response && ex.response.status === 401) {
        toast.error("Only admins can delete a File!");
      } else if (ex.response && ex.response.status === 404) {
        toast.error("This File has already been deleted.");
      } else if (ex.response && ex.response.status === 500) {
        toast.error(ex.response.message);
      }

      this.setState({ images: originalImages });
    }
  };

  // Pagination Event Handler
  handlePageChange = (page) => {
    this.setState({ currentPage: page });
  };

  // PageSize Event Handler
  handleSizeChange = ({ currentTarget: input }) => {
    let pageSize = { ...this.state.pageSize };
    pageSize = input.value;

    this.setState({ pageSize });
  };

  // Filter Event Handler
  handleFolderSelect = ({ currentTarget: input }) => {
    const folder = this.state.folders.find((f) => f._id === input.value);

    this.setState({
      selectedFolder: folder,
      searchQuery: "",
      currentPage: 1,
    });
  };

  // Search Event Handler
  handleSearch = (query) => {
    this.setState({
      searchQuery: query,
      selectedPoi: null,
      currentPage: 1,
    });
  };

  // Sort Event Handler
  handleSort = (sortColumn) => {
    this.setState({ sortColumn });
  };

  // Get state Data
  getData = () => {
    const {
      currentPage,
      pageSize,
      images: allFiles,
      selectedFolder,
      searchQuery,
      sortColumn,
    } = this.state;

    // Calculate StartNumber
    const startNumber = (currentPage - 1) * pageSize;

    // Get Filter Options
    let filtered = allFiles;

    if (searchQuery)
      filtered = allFiles.filter(
        (i) => i.name.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1
      );
    else if (selectedFolder && selectedFolder._id)
      filtered = allFiles.filter((f) => f.parent === selectedFolder._id);

    // Define Sorting Option
    const sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);

    // Define Pagination
    const images = paginate(sorted, currentPage, pageSize);

    return {
      startNumber: startNumber,
      totalCount: filtered.length,
      data: images,
    };
  };

  render() {
    const {
      currentPage,
      pageSize,
      sortColumn,
      searchQuery,
      folders,
      selectedFolder,
      initialSize,
    } = this.state;
    const { startNumber, totalCount, data: images } = this.getData();

    return (
      <div className="col alt-table-responsive mt-1">
        <Header
          icon="bx bx-file-blank"
          title="Files"
          hasButton={true}
          dependency={null}
          path="/dashboard/file/new"
          name="Upload a new File"
        />

        {images.length !== 0 ? (
          <Fragment>
            <div className="row justify-content-between align-items-center pb-1">
              <div className="col-md-6 my-2">
                <Filter
                  name={"dropdown"}
                  label={"Filter"}
                  options={folders}
                  selectedOption={selectedFolder}
                  onChange={this.handleFolderSelect}
                />
              </div>
              <div className="col-sm-12 col-md-5 col-lg-4 col-xl-3 my-2 text-end">
                <Search value={searchQuery} onChange={this.handleSearch} />
              </div>
            </div>

            <div className="row outer">
              <div className="col">
                <FilesTable
                  images={images}
                  sortColumn={sortColumn}
                  startNumber={startNumber}
                  onDelete={this.handleDelete}
                  onSort={this.handleSort}
                  onClick={this.handleClick}
                />
              </div>
            </div>
          </Fragment>
        ) : (
          <div className="row">
            <div className="col-sm-12 align-self-center">
              <Expire delay={5000}>
                <div className="loader">
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </Expire>
            </div>
          </div>
        )}

        <hr />

        <div className="row align-items-center">
          <div className="col-sm-12 col-md col-lg col-xl mr-auto pb-1">
            <Pagination
              itemsCount={totalCount}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={this.handlePageChange}
            />
          </div>
          <div className="col-auto align-middle">
            <TableMeta
              initial={initialSize}
              itemsCount={images.length}
              total={totalCount}
              onChange={this.handleSizeChange}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default Files;
